import axios, { AxiosError } from 'axios';

class ConformXService {
    private baseURL: string;

    constructor(baseURL: string) {
        this.baseURL = baseURL;
    }

    public async isAuthenticated(username: string, password: string): Promise<{ success: boolean; errorMessage?: string }> {
        try {
            const soapRequest = `<?xml version="1.0" encoding="utf-8"?>
<soap12:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap12="http://www.w3.org/2003/05/soap-envelope">
  <soap12:Body>
    <IsAuthenticated xmlns="http://conformx.docutechcorp.com/ConformXWS">
      <username>${username}</username>
      <password>${password}</password>
    </IsAuthenticated>
  </soap12:Body>
</soap12:Envelope>`

            const response = await axios.post(`${this.baseURL}`, soapRequest, {
                headers: {
                    'Content-Type': 'application/soap+xml; charset=utf-8',
                },
            });

            // Parse the SOAP response
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(response.data, "text/xml");
            const resultNode = xmlDoc.getElementsByTagName("Result")[0];

            // Validate the Result node
            if (!resultNode) {
                console.error("Result node is missing in the response.");
                return { 
                    success: false, 
                    errorMessage: "Invalid response from ConformX server. Please contact Docutech support" 
                };
            }
            
            const resultValue = resultNode.textContent?.toLowerCase();
            if (resultValue === "true") {
                return { success: true };
            } else if (resultValue === "false") {
                return { 
                    success: false, 
                    errorMessage: "Authentication Failed: The API Key or API Secret provided is incorrect. Please verify your ConformX credentials and try again." 
                };
            } else {
                console.error("Unexpected Result value: ", resultValue);
                return { 
                    success: false, 
                    errorMessage: `Unexpected authentication response: ${resultValue}. Please contact support.` 
                };
            }
        } catch (error: unknown) {
            console.error("ConformXService IsAuthenticated Error: ", error);
            
            // Type guard to check if error is an AxiosError
            const isAxiosError = (err: unknown): err is AxiosError => {
                return err instanceof AxiosError || 
                       (typeof err === 'object' && err !== null && 
                        ('isAxiosError' in err || ('config' in err && 'request' in err)));
            };
            
            if (isAxiosError(error)) {
                // Handle different types of network/connection errors
                if (error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.response?.status === 404) {
                    return { 
                        success: false, 
                        errorMessage: `Connection Failed. There was no endpoint listening at ${this.baseURL} that could accept the message. This is often caused by an incorrect address or SOAP action.` 
                    };
                } else if (error.message?.includes('timeout')) {
                    return { 
                        success: false, 
                        errorMessage: `Connection Timeout: ConformX server at ${this.baseURL} is not responding. Please check the server status or try again later.` 
                    };
                } else {
                    return { 
                        success: false, 
                        errorMessage: `Connection Error: ${error.message || 'Unknown error occurred while connecting to ConformX server.'}` 
                    };
                }
            } else {
                // Handle non-Axios errors (generic errors, etc.)
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                return { 
                    success: false, 
                    errorMessage: `Unexpected Error: ${errorMessage}` 
                };
            }
        }
    }
}

export default ConformXService;